import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, fecha } from '../utils/formato';
import { creditosPendientes } from '../utils/creditos';
import { juntarTodo, resumen, CLASES } from '../utils/porCobrar';

// Toda la plata que está afuera, en una sola lista.
//
// Los tres montones ya salían cada uno en su sitio —lo prestado en Fincas, el
// queso fiado en Clientes, los vales en Nómina— y ahí se quedan: cuando uno va
// a pagarle a una finca tiene que ver ahí mismo que esa persona le debe, o le
// paga la leche completa.
//
// Esta pantalla es para la OTRA pregunta, la de sentarse a cobrar: «¿cuánta
// plata mía está en la calle y desde cuándo?». No recalcula nada; junta y
// ordena con las mismas funciones ya probadas.
function PorCobrar({ irA }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [clase, setClase] = useState('todo');

  const cargar = useCallback(() => {
    Promise.all([
      axios.get('/api/prestamos-fincas'),
      axios.get('/api/fincas/estado-cuenta'),
      axios.get('/api/clientes/creditos'),
      axios.get('/api/vales-trabajadores'),
      axios.get('/api/trabajadores/estado'),
    ])
      // Si falla, se DICE. Un catch vacío dejaría la pantalla en «no hay nada
      // afuera», que es la misma cara que tendría si de verdad no hubiera nada
      // — y con esa cara nadie sale a cobrar.
      .then(([pr, fi, cr, va, tr]) => {
        setDatos({
          prestamos: pr.data || [],
          fincas: fi.data || [],
          despachos: creditosPendientes(cr.data?.ventas, cr.data?.abonos),
          vales: va.data || [],
          trabajadores: tr.data || [],
        });
        setError('');
      })
      .catch(() => setError('No se pudo cargar. Recargue la página; si sigue igual, avise.'));
  }, []);

  useEffect(cargar, [cargar]);

  if (error) return <div className="aviso malo">{error}</div>;
  if (!datos) return <p style={{ color: 'var(--tinta-tenue)' }}>Cargando…</p>;

  const todas = juntarTodo(datos);
  const r = resumen(todas);
  const filas = clase === 'todo' ? todas : todas.filter(f => f.clase === clase);

  const irAlSitio = (f) => irA(f.clase === 'finca' ? 'fincas'
                            : f.clase === 'cliente' ? 'cartera' : 'nomina');

  return (
    <>
      <div className="encabezado">
        <h1>Por cobrar</h1>
        <p>Toda la plata que está afuera, de la más vieja a la más nueva</p>
      </div>

      {todas.length === 0 ? (
        <div className="bloque">
          <div className="vacio">
            No tiene plata afuera. Nadie le debe, no le ha prestado a ninguna
            finca y no hay vales sin descontar.
          </div>
        </div>
      ) : (
        <>
          <div className="rejilla">
            <div className="dato" style={{ '--color': 'var(--acento-vivo)' }}>
              <div className="rotulo">💸 Está afuera</div>
              <div className="valor cifra">{plata(r.total)}</div>
              <div className="pie">
                en {r.cuantos} {r.cuantos === 1 ? 'cuenta' : 'cuentas'}
              </div>
            </div>
            {/* LA DISTINCIÓN QUE EVITA UNA ILUSIÓN. Solo el queso fiado entra
                como plata; lo demás vuelve rebajando pagos futuros. Un total
                único haría creer que hay millones por entrar donde en realidad
                hay descuentos. */}
            <div className="dato" style={{ '--color': 'var(--bueno)' }}>
              <div className="rotulo">💵 Va a entrar en plata</div>
              <div className="valor cifra">{plata(r.enEfectivo)}</div>
              <div className="pie">queso fiado — el cliente lo paga</div>
            </div>
            <div className="dato" style={{ '--color': 'var(--leche)' }}>
              <div className="rotulo">✂️ Vuelve como descuento</div>
              <div className="valor cifra">{plata(r.enDescuento)}</div>
              <div className="pie">no entra a la caja: rebaja lo que usted paga</div>
            </div>
            {r.masViejo && (
              <div className="dato" style={{ '--color': 'var(--ojo)' }}>
                <div className="rotulo">⏳ Lleva más tiempo afuera</div>
                <div className="valor" style={{ fontSize: '1.2rem' }}>{r.masViejo.quien}</div>
                <div className="pie">
                  {plata(r.masViejo.monto)} · hace {r.masViejo.dias} días
                </div>
              </div>
            )}
          </div>

          <div className="bloque">
            <h2>
              💸 Todo lo que está afuera
              <span className="contador">{filas.length}</span>
            </h2>

            <div className="cuerpo" style={{ paddingBottom: 0 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[['todo', 'Todo', r.total],
                  ['cliente', '🧀 Queso fiado', r.porClase.cliente],
                  ['finca', '🚜 Prestado a fincas', r.porClase.finca],
                  ['trabajador', '👷 Vales', r.porClase.trabajador]]
                  // Un filtro que siempre sale vacío es un botón que estorba.
                  .filter(([id, , monto]) => id === 'todo' || monto > 0)
                  .map(([id, txt, monto]) => (
                    <button key={id} type="button"
                            className={'boton chico ' + (clase === id ? '' : 'suave')}
                            onClick={() => setClase(id)}>
                      {txt} · {plata(monto)}
                    </button>
                  ))}
              </div>
            </div>

            <div className="tabla-caja">
              <table className="datos">
                <thead>
                  <tr>
                    <th>Quién</th>
                    <th>Qué es</th>
                    <th>Desde cuándo</th>
                    <th style={{ textAlign: 'right' }}>Cuánto</th>
                    <th>Cómo vuelve</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map(f => {
                    const C = CLASES[f.clase];
                    return (
                      <tr key={f.id}>
                        <td className="recio">
                          {f.quien}
                          {f.detalle && (
                            <div style={{ fontSize: 12, fontWeight: 400,
                                          color: 'var(--tinta-tenue)' }}>
                              {f.detalle}
                            </div>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {C.ico} {C.que}
                          {f.nota && (
                            <div style={{ fontSize: 12, color: 'var(--tinta-tenue)' }}>
                              {f.nota}
                            </div>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {f.fecha ? fecha(f.fecha) : '—'}
                          {/* Los días, que es lo que decide a quién se llama
                              primero. Una fecha sola obliga a restar. */}
                          {f.dias !== null && (
                            <div style={{ fontSize: 12, fontWeight: 700,
                                          color: f.dias > 60 ? 'var(--malo)'
                                               : f.dias > 30 ? 'var(--ojo)'
                                               : 'var(--tinta-tenue)' }}>
                              {f.dias === 0 ? 'hoy'
                                : f.dias === 1 ? 'hace 1 día'
                                : `hace ${f.dias} días`}
                            </div>
                          )}
                        </td>
                        <td className="num recio">{plata(f.monto)}</td>
                        <td style={{ fontSize: 13, color: 'var(--tinta-tenue)' }}>
                          {C.vuelve}
                        </td>
                        <td>
                          {/* Esta pantalla no cobra: lleva a donde se cobra. Un
                              segundo sitio para anotar el mismo abono son dos
                              caminos que hay que mantener iguales, y el día que
                              uno se quede atrás nadie sabe cuál es el bueno. */}
                          <button className="boton chico suave"
                                  onClick={() => irAlSitio(f)}>
                            Ir
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="cuerpo" style={{ paddingTop: 10 }}>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
                Solo el <strong>queso fiado</strong> entra como plata. Lo prestado
                a las fincas y los vales <strong>no están perdidos</strong>, pero
                tampoco van a entrar a la caja: rebajan lo que usted paga cuando
                le pague la leche o la quincena.
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default PorCobrar;
